import styles from "./action-icon.module.scss";
import { ActionIcon as MantineActionIcon } from "@mantine/core";
import type { ActionIconFactory, IProps } from "./action-icon.types";
import { polymorphicComponentWrapper } from "../../utilities";
import classNames from "classnames";
import { Icon } from "../icon";
import { InteractionIcon } from "../interaction-icon";

/**
 * Action icon component for clickable icon buttons.
 * @param {IconDefinition} props.icon - The icon to display.
 * @param {React.ReactNode} props.children - Optional custom content to render instead of icon.
 * @returns {JSX.Element} The ActionIcon component.
 */
const ActionIcon = polymorphicComponentWrapper<ActionIconFactory, IProps>("ActionIcon", (props, ref) => {
    const { icon, children, ...restProps } = props;
    return (
        <MantineActionIcon ref={ref} classNames={{ root: `action-icon ${styles["container"]}` }} {...restProps}>
            {icon && <Icon icon={icon} />}
            {children}
        </MantineActionIcon>
    );
});

/**
 * Toggle variant of ActionIcon that displays an enabled/disabled state.
 * @param {boolean} props.enabled - Whether the toggle is in the enabled state.
 * @returns {JSX.Element} The ActionIconToggle component.
 */
const ActionIconToggle = polymorphicComponentWrapper<ActionIconFactory, IProps & { enabled?: boolean }>(
    "ActionIconToggle",
    (props, ref) => {
        const { enabled = false, className, ...restProps } = props;
        return (
            <ActionIcon
                ref={ref}
                className={classNames(styles["toggler"], className)}
                data-enabled={enabled}
                {...restProps}
                variant="default"
            />
        );
    }
);

const ActionIconSkeleton = InteractionIcon.Skeleton;

ActionIcon.Group = MantineActionIcon.Group;
ActionIcon.GroupSection = MantineActionIcon.GroupSection;
ActionIcon.Toggle = ActionIconToggle;
ActionIcon.Skeleton = ActionIconSkeleton;

export { ActionIcon, ActionIconToggle, ActionIconSkeleton };
